import { useState, useEffect, useCallback } from 'react';
import { makeApiCall } from '../utils/api';
import { API_PATHS } from '../config/api-endpoints';
import { Profile } from '../types';
import { mockProfiles } from '../data/mockData';

export interface ListProfilesRequest {
    userId?: string;
    limit?: number;
    nextToken?: string;
}

export interface ListProfilesResponse {
    profiles: Array<{
        profileId: string;
        profileName: string;
        ldap: string;
        description: string;
        createdAt: string;
        createdBy: string;
    }>;
    nextToken?: string;
}

interface UseProfilesOptions {
    userId?: string;
    limit?: number;  // Page size for API
    autoFetch?: boolean;
}

interface UseProfilesReturn {
    profiles: Profile[];
    loading: boolean;
    error: string | null;
    hasMore: boolean;
    nextToken: string | null;
    totalCount: number;
    fetchProfiles: (reset?: boolean) => Promise<void>;
    fetchNextPage: () => Promise<void>;
    refresh: () => Promise<void>;
}

/**
 * Check if mock data is enabled from settings
 */
const isMockDataEnabled = (): boolean => {
    const settings = (window as any).__ADDAPT_SETTINGS__;
    return settings?.features?.enableMockData === true;
};

export const useProfiles = (options: UseProfilesOptions = {}): UseProfilesReturn => {
    const { userId, limit = 50, autoFetch = true } = options;
    
    const [profiles, setProfiles] = useState<Profile[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [nextToken, setNextToken] = useState<string | null>(null);
    const [hasMore, setHasMore] = useState(false);
    const [totalCount, setTotalCount] = useState(0);

    // Transform API response to Profile format
    const transformProfile = (apiProfile: any): Profile => {
        return {
            id: apiProfile.profileId,
            name: apiProfile.profileName,
            description: apiProfile.description || '',
            createdBy: apiProfile.createdBy || 'Unknown',
            lastUpdated: apiProfile.createdAt || new Date().toISOString(),
            status: 'active' as const
        };
    };

    // Fetch profiles from API or mock data
    const fetchProfiles = useCallback(async (reset: boolean = false) => {
        if (loading) return;
        
        setLoading(true);
        setError(null);

        try {
            // Use mock data if enabled (standard Amazon practice for local development)
            if (isMockDataEnabled()) {
                console.log('Using mock data for profiles');
                // Simulate network delay
                await new Promise(resolve => setTimeout(resolve, 300));
                
                if (reset) {
                    setProfiles(mockProfiles);
                } else {
                    setProfiles(prev => [...prev, ...mockProfiles]);
                }
                setNextToken(null);
                setHasMore(false);
                setTotalCount(mockProfiles.length);
                return;
            }

            const request: ListProfilesRequest = {
                limit  // Always include limit
            };
            
            // Add userId filter if provided
            if (userId) {
                request.userId = userId;
            }
            
            // Add pagination token if not resetting
            if (!reset && nextToken) {
                request.nextToken = nextToken;
            }

            console.log('Fetching profiles with request:', request);

            const response: ListProfilesResponse = await makeApiCall(
                API_PATHS.PROFILES.LIST,
                'POST',
                request
            );

            console.log('Profiles response:', response);

            // Transform profiles
            const transformedProfiles = response.profiles.map(transformProfile);
            
            if (reset) {
                setProfiles(transformedProfiles);
            } else {
                setProfiles(prev => [...prev, ...transformedProfiles]);
            }
            
            // Update pagination state
            setNextToken(response.nextToken || null);
            setHasMore(!!response.nextToken);
            setTotalCount(prev => reset ? transformedProfiles.length : prev + transformedProfiles.length);
            
        } catch (err: any) {
            console.error('Failed to fetch profiles:', err);
            setError(err.message || 'Failed to load profiles');
            
            // If it's a 404 or the API doesn't exist yet, return empty list
            if (err.message?.includes('404') || err.message?.includes('Not Found')) {
                setProfiles([]);
                setHasMore(false);
                setError(null); // Don't show error for empty state
            }
        } finally {
            setLoading(false);
        }
    }, [userId, nextToken, loading]);

    // Fetch next page
    const fetchNextPage = useCallback(async () => {
        if (!hasMore || loading) return;
        await fetchProfiles(false);
    }, [hasMore, loading, fetchProfiles]);

    // Refresh (reset and fetch)
    const refresh = useCallback(async () => {
        setNextToken(null);
        await fetchProfiles(true);
    }, [fetchProfiles]);

    // Auto-fetch on mount if enabled
    useEffect(() => {
        if (autoFetch) {
            fetchProfiles(true);
        }
    }, [userId]); // Re-fetch when userId changes

    return {
        profiles,
        loading,
        error,
        hasMore,
        nextToken,
        totalCount,
        fetchProfiles,
        fetchNextPage,
        refresh
    };
};

// Hook for fetching a single profile
export const useProfile = (profileId: string | null) => {
    const [profile, setProfile] = useState<Profile | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchProfile = useCallback(async () => {
        if (!profileId) return;
        
        setLoading(true);
        setError(null);

        try {
            const response = await makeApiCall(
                API_PATHS.PROFILES.GET,
                'POST',
                { profileId }
            );

            // The API returns data nested under 'profile' key
            const profileData = response.profile || response;

            const transformedProfile: Profile = {
                id: profileData.profileId,
                name: profileData.profileName,
                description: profileData.description || '',
                createdBy: profileData.createdBy || 'Unknown',
                lastUpdated: profileData.createdAt || new Date().toISOString(),
                status: 'active' as const,
                ldapGroup: profileData.ldap  // Add ldap field
            };

            setProfile(transformedProfile);
        } catch (err: any) {
            console.error('Failed to fetch profile:', err);
            setError(err.message || 'Failed to load profile');
        } finally {
            setLoading(false);
        }
    }, [profileId]);

    useEffect(() => {
        if (profileId) {
            fetchProfile();
        }
    }, [profileId, fetchProfile]);

    return { profile, loading, error, refresh: fetchProfile };
};
